import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { nearestCity } from "@/lib/places";

const LocationContext = createContext(null);

export function LocationProvider({ children }) {
  const [loc, setLoc] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gs_loc")) || { city: "", country: "", coords: null }; }
    catch { return { city: "", country: "", coords: null }; }
  });

  const save = useCallback((next) => {
    setLoc(next);
    localStorage.setItem("gs_loc", JSON.stringify(next));
  }, []);

  const setCity = useCallback((city, country) => {
    save({ ...loc, city, country: country || loc.country });
  }, [loc, save]);

  const requestGPS = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("GPS nuk mbështetet"));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          const nc = nearestCity(coords.lat, coords.lng);
          save({ coords, city: nc?.city || "Afër meje", country: nc?.country || "" });
          resolve(coords);
        },
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });
  }, [save]);

  // Auto-detect current location on load if the browser already granted permission
  useEffect(() => {
    if (!navigator.geolocation) return;
    const go = () => requestGPS().catch(() => {});
    if (!navigator.permissions) { go(); return; }
    navigator.permissions.query({ name: "geolocation" })
      .then((p) => { if (p.state !== "denied") go(); })
      .catch(go);
  }, [requestGPS]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const nc = nearestCity(coords.lat, coords.lng);
        if (!nc?.city) return;
        setLoc((prev) => {
          if (prev.city === nc.city) return prev;
          const next = { coords, city: nc.city, country: nc.country || "" };
          localStorage.setItem("gs_loc", JSON.stringify(next));
          return next;
        });
      },
      () => {},
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 30000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  return (
    <LocationContext.Provider value={{ loc, setCity, requestGPS, clear: () => save({ city: "", country: "", coords: null }) }}>
      {children}
    </LocationContext.Provider>
  );
}

export const useLocation = () => useContext(LocationContext);
